// ────────────────────────────────────────────────────────────────────────────
// Localisation foundation for @my/check.
//
// Single-source-of-truth init for `react-i18next` (the underlying library —
// the folder is named after the broader concept, "localisation", which also
// covers number / date / currency formatting and pluralisation as those land).
// Bundled English resources; other languages get added by dropping a sibling
// JSON under `locales/<code>/` and listing it in `resources` below.
//
// The runtime language is *not* auto-detected from the browser — it is driven
// by the loaded organisation's `languageCode` (see `SyncLanguageWithOrg`).
// English is the boot default and the unconditional fallback for any code we
// don't yet ship translations for.
//
// New strings live in `locales/en/<namespace>.json`. Namespaces split by major
// feature area so the blast radius of adding a new language is contained
// per-feature. Add the new namespace to `resources` below and to the
// typed-keys augmentation in `types.d.ts` — the rest is automatic.
// ────────────────────────────────────────────────────────────────────────────
import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';

import enCommon from './locales/en/common.json';
import enDashboard from './locales/en/dashboard.json';
import enNavigation from './locales/en/navigation.json';
import enChecks from './locales/en/checks.json';
import enOrganisation from './locales/en/organisation.json';
import enResources from './locales/en/resources.json';

/**
 * Languages this build ships translations for. Adding a new locale means:
 *  1. Drop sibling JSON files under `locales/<code>/<namespace>.json`.
 *  2. Add the code to this tuple.
 *  3. Add a top-level entry under `resources` below.
 *
 * The org's `languageCode` is matched against this list (case-insensitive);
 * unrecognised values fall back to English silently rather than rendering
 * missing-key placeholders.
 */
export const SUPPORTED_LANGUAGES = ['en'] as const;
export type SupportedLanguage = (typeof SUPPORTED_LANGUAGES)[number];

export function isSupportedLanguage(code: string): code is SupportedLanguage {
  return (SUPPORTED_LANGUAGES as readonly string[]).includes(code);
}

/** Default + fallback. Kept as a constant so callers don't hard-code 'en'. */
export const DEFAULT_LANGUAGE: SupportedLanguage = 'en';

/**
 * `localStorage` key for the last-known good language. Namespaced per app
 * (`my:check:lng`) because each suite app runs on its own origin and
 * therefore has its own localStorage. The JWT-driven sync
 * (`SyncLanguageWithOrg`) brings the per-app caches into agreement at login
 * by re-applying the org's `languageCode` everywhere it lands.
 *
 * Read on boot to avoid a flash of English while the org payload arrives;
 * written by `SyncLanguageWithOrg` after every org-driven language change
 * so the next boot starts in the right place.
 */
const LANGUAGE_STORAGE_KEY = 'my:check:lng';

/** Strip a region tag (`en-IE` → `en`) before testing the supported set. */
function normaliseCode(raw: string): string {
  return raw.toLowerCase().split('-')[0] ?? '';
}

/** Read a previously-cached language. Returns null on first visit / quota errors. */
function readStoredLanguage(): SupportedLanguage | null {
  try {
    const raw = globalThis.localStorage?.getItem(LANGUAGE_STORAGE_KEY);
    if (!raw) return null;
    const code = normaliseCode(raw);
    return isSupportedLanguage(code) ? code : null;
  } catch {
    // Private-mode browsers / disabled storage / SSR — no cache, no harm.
    return null;
  }
}

/** Best-effort browser hint for first-time visitors with no cache. */
function readBrowserLanguage(): SupportedLanguage | null {
  const raw = globalThis.navigator?.language;
  if (!raw) return null;
  const code = normaliseCode(raw);
  return isSupportedLanguage(code) ? code : null;
}

/**
 * Boot-time resolution: cached preference > browser hint > default.
 * Whatever this returns is overridden by `SyncLanguageWithOrg` once the
 * org loads — but using it for the *initial* render means returning users
 * see their language from the very first paint instead of an English flash.
 *
 * Exported so the app can also pass it to Auth0 (`ui_locales`) when wiring
 * the auth provider, so Auth0's universal login renders in the same
 * language the app boots into.
 */
export function resolveBootLanguage(): SupportedLanguage {
  return readStoredLanguage() ?? readBrowserLanguage() ?? DEFAULT_LANGUAGE;
}

/**
 * Persist the active language. Called by `SyncLanguageWithOrg` after every
 * org-driven `changeLanguage` so the next boot starts on the right foot.
 * Failures (storage disabled / quota) are swallowed — caching is a
 * performance optimisation, not correctness.
 */
export function persistLanguage(code: SupportedLanguage): void {
  try {
    globalThis.localStorage?.setItem(LANGUAGE_STORAGE_KEY, code);
  } catch {
    /* see readStoredLanguage — no-op on storage failures */
  }
}

let initPromise: Promise<void> | null = null;

/**
 * Initialise the localisation layer (i18next under the hood) exactly once.
 * Subsequent calls return the same promise so concurrent boots (HMR,
 * StrictMode double-invoke, etc.) don't double-init. Call from `main.tsx`
 * `bootstrap()` *before* `createRoot().render()`.
 *
 * Returns `Promise<void>` rather than the i18n instance — callers (the
 * bootstrap, mostly) just need to await readiness. The instance itself is
 * already importable as a named export (`{ i18n }`) for the rare case that
 * needs it directly.
 */
export function initLocalisation(): Promise<void> {
  if (initPromise) return initPromise;
  // Local const + assign-then-return avoids the `Promise<void> | null`
  // narrowing issue that bites a direct `return initPromise` after the
  // assignment.
  const p = i18n
    .use(initReactI18next)
    .init({
      resources: {
        en: {
          common: enCommon,
          dashboard: enDashboard,
          navigation: enNavigation,
          checks: enChecks,
          organisation: enOrganisation,
          resources: enResources,
        },
      },
      // Boot language = cache > browser hint > default. The org payload
      // overrides this once it lands (see `SyncLanguageWithOrg`). The
      // fallback stays English unconditionally so any key missing from
      // a translation bundle reads through to the English copy.
      lng: resolveBootLanguage(),
      fallbackLng: DEFAULT_LANGUAGE,
      defaultNS: 'common',
      ns: ['common', 'dashboard', 'navigation', 'checks', 'organisation', 'resources'],
      // React already escapes interpolated values — letting i18next
      // double-escape would mangle anything containing `<`, `&`, etc.
      interpolation: { escapeValue: false },
      // We init synchronously with bundled resources, so there's no
      // async load step to suspend on. Leaving Suspense off avoids a
      // SuspenseBoundary requirement on every translated component.
      react: { useSuspense: false },
      returnNull: false,
    })
    // `init()` resolves with the `t` function; the bootstrap doesn't
    // need it. Map to void so the public signature stays focused on
    // "i18n is ready".
    .then(() => undefined);
  initPromise = p;
  return p;
}

export { i18n };
export default i18n;
